"use client";

import { useEffect, useMemo, useState } from "react";
import { fetchTestimonials } from "@/lib/api";
import type { Testimonial } from "@/types/page";

type InlineTestimonial = {
  author?: string;
  role?: string;
  avatarUrl?: string;
  rating?: number;
  body?: string;
  sourceLink?: string;
};

export function TestimonialsBlock({ content }: { content: Record<string, unknown> }) {
  const heading = String(content.heading ?? "What clients say");
  const intro = String(content.intro ?? "");
  const source = String(content.source ?? "curated");
  const limit = Number(content.limit ?? 6);
  const inlineItems = useMemo(() => {
    const items = Array.isArray(content.items) ? content.items : [];
    return items
      .map((item) => item as InlineTestimonial)
      .filter((item) => item.author && item.body)
      .slice(0, Number.isFinite(limit) && limit > 0 ? limit : 6);
  }, [content.items, limit]);
  const [items, setItems] = useState<Testimonial[]>([]);

  useEffect(() => {
    if (source !== "curated") return;
    fetchTestimonials(Number.isFinite(limit) && limit > 0 ? limit : 6)
      .then(setItems)
      .catch(() => setItems([]));
  }, [source, limit]);

  const visible = source === "curated" ? items : inlineItems;
  if (!visible.length && !heading && !intro) {
    return null;
  }

  return (
    <section className="w-full bg-[var(--color-surface-muted)] py-12">
      <div className="mx-auto max-w-6xl px-4">
        {(heading || intro) && (
          <div className="mb-8 max-w-3xl">
            {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
            {intro && <p className="mt-3 text-slate-700">{intro}</p>}
          </div>
        )}
        {visible.length ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((testimonial, index) => (
              <div
                key={"id" in testimonial ? testimonial.id : `${testimonial.author}-${index}`}
                className="rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm"
              >
                <div className="mb-4 flex items-center gap-3">
                  {testimonial.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={testimonial.avatarUrl}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)] text-lg font-semibold text-white"
                      aria-hidden
                    >
                      {String(testimonial.author ?? "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-[var(--color-text)]">{testimonial.author}</h3>
                    {testimonial.role && <p className="text-sm text-slate-600">{testimonial.role}</p>}
                  </div>
                </div>
                {testimonial.rating && (
                  // WCAG 2.5.3: accessible name must contain the visible text ("4/5").
                  <p className="mb-3 text-sm font-medium text-[var(--color-primary)]" aria-label={`${testimonial.rating}/5 stars`}>
                    {testimonial.rating}/5
                  </p>
                )}
                <p className="text-sm leading-6 text-slate-700">{testimonial.body}</p>
                {testimonial.sourceLink && (
                  <a
                    href={testimonial.sourceLink}
                    className="mt-4 inline-block text-sm font-medium text-[var(--color-primary)] hover:underline"
                  >
                    Source
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-600">No approved testimonials yet.</p>
        )}
      </div>
    </section>
  );
}
